import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isSameData } from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { VersionRange } from '../core/version-range.js';
import { LegacyReceipt } from './invoicing/legacy-receipt.js';
import { OfflineInvoicingDetails } from './invoicing/offline-invoicing-details.js';

export class OfflinePaymentDetails extends DataModel {
  static readonly TYPE_ID: string = '4a9b0af9989a4bac931551b7c315039f';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly invoicingDetails: OfflineInvoicingDetails | null,
  ) {
    super();
  }

  get $typeId(): string {
    return OfflinePaymentDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OfflinePaymentDetails.SCHEMA_VERSION);
  }

  static create(args?: {
    invoicingDetails?: OfflineInvoicingDetails | null,
  }): OfflinePaymentDetails {
    return new OfflinePaymentDetails(
        args?.invoicingDetails ?? null,
    );
  }

  static empty(): OfflinePaymentDetails {
    return this.create();
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflinePaymentDetails {
    return OfflinePaymentDetails.create({
      invoicingDetails: context.fromDataOrNull<OfflineInvoicingDetails>(MapUtils.getRecordOrNull(map, 'invoicingDetails')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'invoicingDetails': context.toDataOrNull(this.invoicingDetails),
    };
  }

  clone(): this {
    return OfflinePaymentDetails.create({
      invoicingDetails: this.invoicingDetails?.clone(),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.invoicingDetails, this.invoicingDetails);
  }

  copyWith(data: Partial<OfflinePaymentDetails>): OfflinePaymentDetails {
    return OfflinePaymentDetails.create({
      invoicingDetails: data.invoicingDetails ?? this.invoicingDetails?.clone(),
    });
  }
}

export class OfflinePaymentDetailsBuilder extends DataModelBuilder<OfflinePaymentDetails> {
  build(context: SerializationContext, map: SerializedData): OfflinePaymentDetails {
    return OfflinePaymentDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflinePaymentDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflinePaymentDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we switched to the standalone `invoicingDetails` field.
    if(version < 2) {
      const pastReceipts = MapUtils.getList(map, 'pastReceipts', (x) => context.fromData<LegacyReceipt>(JSONUtils.decodeRecord(x)));
      const currentReceipt = context.fromDataOrNull<LegacyReceipt>(MapUtils.getRecordOrNull(map, 'currentReceipt'));

      const invoicingDetails = OfflineInvoicingDetails.fromLegacy(currentReceipt, pastReceipts);
      map['invoicingDetails'] = context.toDataOrNull(invoicingDetails);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
